// Recipient list for the Send for Signature dialog — shared by the Documents tab
// (TransactionDocuments) and the TDS/SPQ prospect stages on the Timeline (LoneWolfStageFormCard).

/**
 * Build the signer list for the signature modal.
 * Agent (current user) is placed first, followed by the role-matched participants
 * (BUYER for buyer agents, SELLER for listing agents) so the dialog reflects the
 * exact order in which DocuSign will route the envelope.
 */
export const filterParticipantsForSignature = (list, userRole) => {
  const agentRoles = ['BUYER_AGENT', 'BUYERS_AGENT', 'CO_BUYERS_AGENT', 'CO_BUYER_AGENT',
                      'LISTING_AGENT', 'CO_LISTING_AGENT', 'SELLERS_AGENT'];
  const isTcCaller = userRole === 'TRANSACTION_COORDINATOR';
  let targetRoles = [];
  if (['BUYER_AGENT', 'BUYERS_AGENT', 'CO_BUYERS_AGENT', 'CO_BUYER_AGENT'].includes(userRole)) {
    targetRoles = ['BUYER'];
  } else if (['LISTING_AGENT', 'CO_LISTING_AGENT', 'SELLERS_AGENT'].includes(userRole)) {
    targetRoles = ['SELLER'];
  } else if (isTcCaller) {
    targetRoles = ['BUYER', 'SELLER'];
  }

  if (targetRoles.length > 0) {
    const currentUserEmail = (() => {
      try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
    })();

    // Agent = current user matched by email, or fall back to role match.
    // A TC caller is never matched here by email — the TC is a transaction
    // participant now, and TCs must never appear as an envelope signer, so
    // only the actual agent-role participant(s) qualify for a TC caller.
    const agentParticipants = list.filter(p => {
      if (!isTcCaller && currentUserEmail) {
        return (p.email || '').toLowerCase() === currentUserEmail.toLowerCase();
      }
      return agentRoles.includes((p.role || p.participantRole || '').toUpperCase());
    });

    const otherParticipants = list.filter(p => {
      const role = (p.role || p.participantRole || '').toUpperCase();
      return targetRoles.includes(role);
    });

    let combined = [...agentParticipants, ...otherParticipants];
    if (combined.length === 0) combined = list;
    // Defense-in-depth: strip the TC caller out even from the raw-list
    // fallback above, so a TC can never end up as a selectable signer.
    if (isTcCaller && currentUserEmail) {
      combined = combined.filter(p => (p.email || '').toLowerCase() !== currentUserEmail.toLowerCase());
    }
    return combined;
  }
  return list;
};

/** Normalizes the participants endpoint's response, which comes back in several shapes. */
export const extractParticipantsList = (data) => {
  if (Array.isArray(data)) return data;
  for (const key of ['data', 'participants', 'content', 'items', 'list']) {
    if (data && Array.isArray(data[key])) return data[key];
  }
  return [];
};
